import { useState, useCallback, useMemo, memo, type ChangeEvent, type FormEvent } from "react";
import { useLocation, Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { UserButton } from "@clerk/clerk-react";
import { useQuery, useMutation } from "convex/react";
import { api } from "../../../convex/_generated/api";
import { toast } from "sonner";
import { Upload, X, Loader2, TrendingUp } from "lucide-react";
import { useTranslation } from "react-i18next";
import { SafetyTermsDialog } from "@/components/SafetyTermsDialog";
import type { Id } from "../../../convex/_generated/dataModel";
import { Navbar } from "@/components/Navbar";

type CategoryRecord = {
  _id: Id<"categories">;
  name: string;
};

type DemandSignalRecord = {
  _id: string;
  keyword: string;
  categoryId?: Id<"categories">;
  wantCount: number;
};

// Memoized image preview component
const ImagePreview = memo(function ImagePreview({
  src,
  index,
  onRemove,
  deleteLabel,
}: {
  src: string;
  index: number;
  onRemove: (index: number) => void;
  deleteLabel: string;
}) {
  const handleRemove = useCallback(() => {
    onRemove(index);
  }, [index, onRemove]);

  return (
    <div className="relative group">
      <img
        src={src}
        alt={`Preview ${index}`}
        className="w-full h-24 object-cover rounded-md"
        loading="lazy"
      />
      <button
        type="button"
        onClick={handleRemove}
        title={deleteLabel}
        className="absolute top-1 right-1 bg-destructive text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
});

// Memoized demand signal button
const DemandSignalButton = memo(function DemandSignalButton({
  signal,
  onApply,
}: {
  signal: DemandSignalRecord;
  onApply: (signal: DemandSignalRecord) => void;
}) {
  const handleClick = useCallback(() => {
    onApply(signal);
  }, [signal, onApply]);

  return (
    <button
      type="button"
      onClick={handleClick}
      className="w-full text-left bg-background p-3 rounded-md border hover:border-primary transition-colors flex justify-between items-center group"
    >
      <span className="font-medium group-hover:text-primary transition-colors">{signal.keyword}</span>
      <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
        {signal.wantCount > 0 ? `${signal.wantCount} wants` : "Hot"}
      </span>
    </button>
  );
});

export default function PostAd() {
  const { t } = useTranslation();
  const [, setLocation] = useLocation();
  const [formData, setFormData] = useState({
    categoryId: "",
    title: "",
    description: "",
    price: "",
    minPrice: "",
    location: "",
    contactEmail: "",
    contactPhone: "",
    isPersonalConnection: false,
    // New Fields
    relationshipType: "",
    wants: "",
    needs: "",
    financialExpectation: "none",
    financialDetails: "",
  });
  const [images, setImages] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSafetyDialog, setShowSafetyDialog] = useState(false);

  // Memoize query args
  const demandQueryArgs = useMemo(() => ({ limit: 5 }), []);

  // Convex queries and mutations
  const categories = useQuery(api.interactions.getCategories);
  const categoryOptions = useMemo(() => (categories ?? []) as CategoryRecord[], [categories]);
  const demandSignals = useQuery(api.wanted.getDemandSignals, demandQueryArgs);
  const demandOptions = useMemo(() => (demandSignals ?? []) as DemandSignalRecord[], [demandSignals]);
  const createAdMutation = useMutation(api.ads.createAd);
  const user = useQuery(api.auth.getCurrentUser);

  // Stable callback for input changes
  const handleInputChange = useCallback((
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const target = e.target;
    const { name, value } = target;
    const isCheckbox = target instanceof HTMLInputElement && target.type === "checkbox";
    const checked = isCheckbox ? target.checked : undefined;

    if (name === "isPersonalConnection" && checked) {
      if (user && !user.safetyTermsAcceptedAt) {
        setShowSafetyDialog(true);
        return;
      }
    }

    setFormData((prev) => ({
      ...prev,
      [name]: isCheckbox ? checked : value,
    }));
  }, [user]);

  // Stable callback for category change
  const handleCategoryChange = useCallback((value: string) => {
    const category = categoryOptions.find(c => c._id === value);
    const isPersonal = (category as any)?.isPersonalConnection || false;

    if (isPersonal && user && !user.safetyTermsAcceptedAt) {
      setShowSafetyDialog(true);
      // We still set the category, but we'll show the dialog. 
      // If they decline, they can't post anyway.
    }

    setFormData((prev) => ({
      ...prev,
      categoryId: value,
      isPersonalConnection: isPersonal
    }));
  }, [categoryOptions, user]);

  // Stable callback for terms accepted
  const handleTermsAccepted = useCallback(() => {
    setFormData((prev) => ({ ...prev, isPersonalConnection: true }));
    toast.success("Terms accepted. Personal Connection enabled.");
  }, []);

  // Stable callback for safety dialog close
  const handleSafetyDialogCancel = useCallback(() => {
    setShowSafetyDialog(false);
  }, []);

  // Stable callback for image upload
  const handleImageUpload = useCallback(async (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    setUploading(true);
    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];

        // Create a data URL for preview
        const reader = new FileReader();
        reader.onload = (event) => {
          setImages((prev) => [...prev, event.target?.result as string]);
        };
        reader.readAsDataURL(file);
      }
      toast.success("Images added");
    } catch (error) {
      toast.error("Failed to upload images");
    } finally {
      setUploading(false);
    }
  }, []);

  // Stable callback for image removal
  const removeImage = useCallback((index: number) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
  }, []);

  // Stable callback for applying demand signal
  const handleApplyDemandSignal = useCallback((signal: DemandSignalRecord) => {
    setFormData((prev) => ({
      ...prev,
      title: signal.keyword.charAt(0).toUpperCase() + signal.keyword.slice(1),
      categoryId: signal.categoryId || "",
      description: `I have a ${signal.keyword} available for sale...`,
    }));
  }, []);

  // Stable callback for cancel
  const handleCancel = useCallback(() => {
    setLocation("/dashboard");
  }, [setLocation]);

  // Stable callback for form submission
  const handleSubmit = useCallback(async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!formData.categoryId || !formData.title || !formData.description) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);
    try {
      await createAdMutation({
        categoryId: formData.categoryId as Id<"categories">,
        title: formData.title,
        description: formData.description,
        price: formData.price ? parseFloat(formData.price) : undefined,
        minPrice: formData.minPrice ? parseFloat(formData.minPrice) : undefined,
        location: formData.location || undefined,
        images: images,
        isPersonalConnection: formData.isPersonalConnection,
        contactEmail: formData.contactEmail || undefined,
        contactPhone: formData.contactPhone || undefined,
        personalConnectionDetails: formData.isPersonalConnection ? {
          relationshipType: formData.relationshipType || "Friendship",
          wants: formData.wants || undefined,
          needs: formData.needs || undefined,
          financialExpectation: (formData.financialExpectation as "none" | "shared_expenses" | "allowance_gifting" | "negotiable") || "none",
          financialDetails: formData.financialDetails || undefined,
        } : undefined,
      });

      toast.success("Ad posted successfully!");
      setLocation("/dashboard");
    } catch (error: any) {
      toast.error(error.message || "Failed to post ad");
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, images, createAdMutation, setLocation]);

  const isLoadingCategories = categories === undefined;

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <div className="container py-8 flex flex-col lg:flex-row gap-8">
        <div className="flex-1 max-w-2xl">
          <h1 className="mb-8">{t('postAd.title')}</h1>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium mb-2">{t("postAd.category")} *</label>
              <Select
                value={formData.categoryId}
                onValueChange={handleCategoryChange}
                disabled={isLoadingCategories}
              >
                <SelectTrigger>
                  <SelectValue placeholder={isLoadingCategories ? "Loading categories..." : t("postAd.selectCategory")} />
                </SelectTrigger>
                <SelectContent>
                  {categoryOptions.map((cat) => (
                    <SelectItem key={cat._id} value={cat._id}>
                      <span className="flex items-center gap-2">
                        {cat.name}
                        {(cat as any).isPersonalConnection && (
                          <span className="text-xs bg-primary/10 text-primary px-1.5 py-0.5 rounded ml-2">Web3</span>
                        )}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {formData.isPersonalConnection && (
                <div className="mt-2 p-3 bg-blue-50/50 dark:bg-blue-900/20 rounded-md border border-blue-100 dark:border-blue-800 text-sm flex items-start gap-2">
                  <div className="mt-0.5">ℹ️</div>
                  <div>
                    <p className="font-medium text-blue-900 dark:text-blue-200">Web3 Verification Required</p>
                    <p className="text-blue-700 dark:text-blue-300">
                      Posting in this category requires a verified Web3 wallet to prevent spam and ensure safety.
                    </p>
                  </div>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">{t("postAd.adTitle")} *</label>
              <Input
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder={t("postAd.titlePlaceholder")}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">{t("postAd.description")} *</label>
              <Textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder={t("postAd.descriptionPlaceholder")}
                rows={6}
                required
              />
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="block text-sm font-medium mb-2">{t("postAd.price")} *</label>
                <div className="relative">
                  <span className="absolute left-3 top-2.5 text-muted-foreground">$</span>
                  <Input
                    type="number"
                    name="price"
                    value={formData.price}
                    onChange={handleInputChange}
                    placeholder="0.00"
                    className="pl-7"
                    min="0"
                    step="0.01"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="flex items-center gap-2 text-sm font-medium mb-2 text-indigo-600 dark:text-indigo-400">
                  Start AI Negotiator
                  <span className="text-xs bg-indigo-100 text-indigo-800 px-2 py-0.5 rounded-full">New</span>
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-2.5 text-muted-foreground">$</span>
                  <Input
                    type="number"
                    name="minPrice"
                    value={formData.minPrice}
                    onChange={handleInputChange}
                    placeholder="Lowest acceptable"
                    className="pl-7 border-indigo-200 focus-visible:ring-indigo-500"
                    min="0"
                    step="0.01"
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  If set, our AI will automatically negotiate with buyers down to this price.
                </p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">{t("postAd.location")}</label>
              <Input
                name="location"
                value={formData.location}
                onChange={handleInputChange}
                placeholder={t("location.cityState")}
              />
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="block text-sm font-medium mb-2">{t("postAd.email")}</label>
                <Input
                  name="contactEmail"
                  type="email"
                  value={formData.contactEmail}
                  onChange={handleInputChange}
                  placeholder="your@email.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">{t("postAd.phone")}</label>
                <Input
                  name="contactPhone"
                  value={formData.contactPhone}
                  onChange={handleInputChange}
                  placeholder="+1 (555) 000-0000"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">{t("postAd.images")}</label>
              <div className="border-2 border-dashed border-border rounded-lg p-6 text-center">
                <Upload className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                <p className="text-sm text-muted-foreground mb-2">{t("postAd.dragDrop")}</p>
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handleImageUpload}
                  disabled={uploading}
                  className="hidden"
                  id="image-input"
                />
                <label htmlFor="image-input">
                  <Button type="button" variant="outline" disabled={uploading}>
                    {uploading ? t("common.uploading") : t("postAd.chooseImages")}
                  </Button>
                </label>
              </div>

              {images.length > 0 && (
                <div className="grid grid-cols-3 gap-4 mt-4">
                  {images.map((img, idx) => (
                    <ImagePreview
                      key={idx}
                      src={img}
                      index={idx}
                      onRemove={removeImage}
                      deleteLabel={t("common.delete")}
                    />
                  ))}
                </div>
              )}
            </div>

            <div className="flex items-start gap-3 p-4 bg-accent/5 rounded-lg border border-accent/20">
              <input
                type="checkbox"
                name="isPersonalConnection"
                id="isPersonalConnection"
                checked={formData.isPersonalConnection}
                onChange={handleInputChange}
                className="w-5 h-5 accent-primary mt-1"
              />
              <div>
                <label htmlFor="isPersonalConnection" className="font-semibold cursor-pointer">
                  {t("postAd.personalConnection")}
                </label>
                <p className="text-sm text-muted-foreground">{t("postAd.personalConnectionDesc")}</p>
              </div>
              <SafetyTermsDialog
                open={showSafetyDialog}
                onOpenChange={setShowSafetyDialog}
                onAccept={handleTermsAccepted}
                onCancel={handleSafetyDialogCancel}
              />
            </div>

            <div className="flex gap-4">
              <Button type="submit" size="lg" disabled={isSubmitting} className="flex-1">
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {t("postAd.posting")}
                  </>
                ) : (
                  t("postAd.submit")
                )}
              </Button>
              <Button type="button" variant="outline" size="lg" onClick={handleCancel}>
                {t("common.cancel")}
              </Button>
            </div>
          </form>
        </div>

        <div className="w-full lg:w-80 space-y-6">
          <div className="card-elegant p-6 bg-primary/5 border-primary/20 sticky top-24">
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp className="w-5 h-5 text-primary" />
              <h3 className="font-semibold text-lg">{t("home.trending")}</h3>
            </div>

            <p className="text-sm text-muted-foreground mb-4">
              People are looking for these items right now. Post an ad to match!
            </p>

            <div className="space-y-3">
              {demandSignals ? (
                demandOptions.length > 0 ? (
                  demandOptions.map((signal) => (
                    <DemandSignalButton
                      key={signal._id}
                      signal={signal}
                      onApply={handleApplyDemandSignal}
                    />
                  ))
                ) : (
                  <p className="text-sm text-muted-foreground italic">No trending items yet.</p>
                )
              ) : (
                <div className="flex justify-center py-4">
                  <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
